import AdvancedInput, { Transformation } from "@edaz/edazkit/controls/AdvancedInput";
import { detectAccountType, startsWithLetter } from "@edaz/utils/bankAccount";
import { Input } from "@frui.ts/bootstrap";
import type { CommonInputProps } from "@frui.ts/bootstrap/dist/controls/commonInputProps";
import type { BindingProperty } from "@frui.ts/helpers";
import { bound } from "@frui.ts/helpers";
import { getValidationMessage } from "@frui.ts/validation";
import type { IBindingProps } from "@frui.ts/views";
import { BindingComponent, getValue, setValue } from "@frui.ts/views";
import classNames from "classnames";
import BankAccount from "entities/bankAccount";
import { IBankAccount } from "entities/types";
import { action, computed } from "mobx";
import { Observer } from "mobx-react-lite";
import React from "react";
import type { FormControlProps } from "react-bootstrap";
import { Form } from "react-bootstrap";
import CzechAccountInput from "./czechAccountInput";
import IbanInput from "./ibanInput";
import { BankAccountFormatType } from "@edaz/utils/bankAccount/types";

export interface AccountNumberOrIbanAndBankCodeInputProps {
  bankCodeProperty: "bank" | "bankCode";
  accountNumberProperty: "accountNumber" | "bankAccountNumber";
  mode: "object" | "property";
  autoComplete?: boolean;
}

export default class AccountNumberOrIbanAndBankCodeInput<TTarget> extends BindingComponent<
  TTarget,
  AccountNumberOrIbanAndBankCodeInputProps & FormControlProps & CommonInputProps & IBindingProps<TTarget>
> {
  state = {
    hasFocus: false,
  };

  static defaultProps = {
    bankCodeProperty: "bank",
    accountNumberProperty: "accountNumber",
    mode: "property",
  };

  @computed
  protected get value() {
    const value = getValue(this.props.target as any as TTarget, this.props.property);

    const bankAccount = new BankAccount();
    if (value) {
      bankAccount.accountNumber = value[this.props.accountNumberProperty];
      bankAccount.bank = value[this.props.bankCodeProperty];
    }

    return bankAccount;
  }

  @bound
  setFocus() {
    this.setState({ hasFocus: true });
  }

  @bound
  unsetFocus() {
    this.setState({ hasFocus: false });
  }

  set accountNumber(accountNumber: string) {
    this.setValue({ accountNumber, bank: this.value?.bank });
  }

  @computed
  get accountNumber() {
    return this.value?.accountNumber;
  }

  set bank(bank: string | undefined) {
    this.setValue({ accountNumber: this.value?.accountNumber, bank });
  }

  @computed
  get bank() {
    return this.value?.bank;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  @bound protected renderInner() {
    const { target, property } = this.props as { target: TTarget; property: keyof TTarget & string }; // the cast is a TS compiler workaround
    const { noValidation, errorMessage, disabled, autoComplete } = this.inheritedProps;
    const errors = [];
    const baseValidationError = noValidation !== true && (errorMessage || getValidationMessage(target, property));
    const accountNumberError = getValidationMessage(target[property] as any, this.props.accountNumberProperty);
    const bankError = getValidationMessage(target[property] as any, this.props.bankCodeProperty);

    if (baseValidationError) {
      errors.push(baseValidationError);
    }

    if (accountNumberError) {
      errors.push(accountNumberError);
    }

    if (bankError) {
      errors.push(bankError);
    }

    const fullErrorMessage = errors.length ? errors.join(", ") : undefined;

    const value = this.value as IBankAccount | undefined;
    const hasFocus = this.state.hasFocus;
    const newTarget = this as any;
    const renderBankCodeSeparately =
      (value?.bank !== undefined && !value?.accountNumber) ||
      (value?.bank !== undefined && value.accountNumber !== undefined && value.accountNumber.length < 3);
    const accountType = value?.accountNumber && detectAccountType(value.accountNumber);

    if (renderBankCodeSeparately || accountType === BankAccountFormatType.IBAN || accountType === BankAccountFormatType.OTHER) {
      return (
        <div className="form-row">
          <div className="col-8">
            {accountType === BankAccountFormatType.IBAN ? (
              <IbanInput
                onFocus={this.setFocus}
                onBlur={this.unsetFocus}
                className={classNames(this.props.className, { "is-invalid": accountNumberError })}
                autoFocus={hasFocus && startsWithLetter(value?.accountNumber ?? "")}
                target={newTarget}
                property="accountNumber"
                disabled={disabled}
                autoComplete={autoComplete}
                placeholder="IBAN"
                isInvalid={!!accountNumberError}
              />
            ) : (
              <AdvancedInput
                target={newTarget}
                property="accountNumber"
                onValueChanged={this.handleValueChanged}
                className={classNames(this.props.className, { "is-invalid": accountNumberError })}
                onFocus={this.setFocus}
                onBlur={this.unsetFocus}
                transformation={Transformation.UpperCased}
                autoFocus={hasFocus}
                disabled={disabled}
                autoComplete={autoComplete}
                errorMessage={accountNumberError ? " " : undefined}
              />
            )}
          </div>
          <div className="col-4">
            <Input
              className={classNames(this.props.className, { "is-invalid": bankError })}
              target={newTarget}
              onFocus={this.setFocus}
              onBlur={this.unsetFocus}
              autoFocus={hasFocus && renderBankCodeSeparately}
              property="bank"
              autoComplete={autoComplete ? undefined : "new-password"}
              disabled={disabled}
              placeholder="Kód"
            />
          </div>
          <div className="col-12">
            {fullErrorMessage && (
              <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
                {fullErrorMessage}
              </Form.Control.Feedback>
            )}
          </div>
        </div>
      );
    } else if (accountType === BankAccountFormatType.CZECH) {
      return (
        <CzechAccountInput
          target={target}
          property={property as BindingProperty<TTarget>}
          bankCodeProperty={this.props.bankCodeProperty}
          accountNumberProperty={this.props.accountNumberProperty}
          mode={this.props.mode}
          skipMask={true}
          autoComplete={autoComplete}
          autoFocus={hasFocus}
          onFocus={this.setFocus}
          onBlur={this.unsetFocus}
          disabled={disabled}
          className={this.props.className}
        />
      );
    } else {
      return (
        <>
          <AdvancedInput
            target={newTarget}
            property="accountNumber"
            onValueChanged={this.handleValueChanged}
            disabled={disabled}
            onFocus={this.setFocus}
            autoComplete={autoComplete}
            onBlur={this.unsetFocus}
            transformation={Transformation.UpperCased}
            placeholder={this.props.placeholder}
            className={classNames(this.props.className, { "is-invalid": fullErrorMessage })}
          />
          {fullErrorMessage && (
            <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
              {fullErrorMessage}
            </Form.Control.Feedback>
          )}
        </>
      );
    }
  }

  @bound protected handleValueChanged(value?: string) {
    let newValue = value;

    if (newValue && startsWithLetter(newValue)) {
      newValue = newValue.trim().replaceAll(" ", "");
      this.setValue({
        accountNumber: newValue,
        bank: undefined,
      });
    } else if (newValue && !startsWithLetter(newValue)) {
      newValue = newValue.trim();
      const [accountNumber, bank] = newValue.split("/");
      const objectValue: IBankAccount = {
        accountNumber: accountNumber.replace("-", ""),
        bank,
      };
      this.setValue(objectValue);
    } else if (newValue && /^\//.test(newValue)) {
      const objectValue: IBankAccount = {
        accountNumber: "",
        bank: "",
      };
      this.setValue(objectValue);
    }
  }

  render() {
    return <Observer render={this.renderInner} />;
  }

  @action.bound
  protected setValue(value: IBankAccount) {
    const { bankCodeProperty, accountNumberProperty, target, property, onValueChanged, mode } = this.props;

    if (mode === "object") {
      const bankAccount = new BankAccount();
      if (value.accountNumber !== undefined) {
        bankAccount.accountNumber = value.accountNumber;
      }
      if (value.bank !== undefined) {
        bankAccount.bank = value.bank;
      }

      setValue(target, property, bankAccount);
    } else {
      if ((target as any)[property] === undefined) {
        (target as any)[property] = new BankAccount();
      }

      setValue((target as any)[property], accountNumberProperty, value.accountNumber);
      setValue((target as any)[property], bankCodeProperty, value.bank);
    }

    if (property && target) {
      onValueChanged?.(value, property, target);
    }
  }
}
